import {
  faExclamationCircle,
  faInfoCircle,
  faTimesCircle,
  type IconDefinition,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import styled, { css } from 'styled-components';
import { Heading } from 'src/components/text/heading';
import { useIsNewDesignActive } from 'src/pages/new-design/is-new-design';
import { Colors } from 'src/styles';
import { cn } from 'src/utils/cn';

type AlertProps = {
  type: 'info' | 'warning' | 'error';
  title?: ReactNode;
} & ComponentPropsWithoutRef<'div'>;

const themes = {
  info: css`
    background-color: ${Colors.brandLight4};
    color: ${Colors.brand};
  `,
  warning: css`
    background-color: #fcf8e3;
    color: #8a6d3b;
  `,
  error: css`
    background-color: ${Colors.errorLighter};
    color: ${Colors.error};
  `,
};

const StyledAlert = styled.div<{ type: 'info' | 'warning' | 'error' }>`
  padding: 1.5rem;
  ${({ type }) => themes[type]};
`;

const label = {
  warning: 'Warning',
  error: 'Error',
  info: 'Info',
};

const LegacyAlert: FC<AlertProps> = ({ type, children, title, ...rest }) => {
  return (
    <StyledAlert type={type} {...rest}>
      {title ? (
        <div
          className={`
            grid items-center gap-md
            legacy:gap-[1rem]
          `}
        >
          <Heading mode="card">
            <FontAwesomeIcon
              icon={type === 'info' ? faInfoCircle : faExclamationCircle}
              aria-hidden
            />{' '}
            {title}
          </Heading>
          <div>{children}</div>
        </div>
      ) : (
        <>
          <FontAwesomeIcon
            icon={type === 'info' ? faInfoCircle : faExclamationCircle}
            aria-hidden
          />{' '}
          <strong>{label[type]}</strong> {children}
        </>
      )}
    </StyledAlert>
  );
};

const icon: Record<AlertProps['type'], IconDefinition> = {
  info: faInfoCircle,
  warning: faExclamationCircle,
  error: faTimesCircle,
} as const;

const classNames: Record<AlertProps['type'], string> = {
  info: cn(`bg-background-support-info-minimal text-foreground-support-info`),
  warning: cn(
    `bg-background-support-warning-minimal text-foreground-support-warning`
  ),
  error: cn(
    `bg-background-support-error-minimal text-foreground-support-error`
  ),
} as const;

const NewAlert: FC<AlertProps> = ({ type, children, title, ...rest }) => {
  return (
    <div
      className={cn(
        classNames[type],
        `grid grid-cols-[auto_1fr] gap-lg rounded-xs px-xl py-lg`
      )}
      {...rest}
    >
      <FontAwesomeIcon icon={icon[type]} size="lg" aria-hidden />

      <div className={cn(`grid gap-sm`)} {...rest}>
        {title && (
          <Heading mode="sub-section" className={cn(`text-inherit!`)}>
            {title}
          </Heading>
        )}

        <div>{children}</div>
      </div>
    </div>
  );
};

export const Alert: FC<AlertProps> = (props) => {
  const isNewDesignActive = useIsNewDesignActive();

  return isNewDesignActive ? (
    <NewAlert {...props} />
  ) : (
    <LegacyAlert {...props} />
  );
};
